// Motor Central de Médias — Painel integrado à Central de Notas
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { isAdmin } from '@/lib/auth';
import {
  CheckCircle2, AlertTriangle, XCircle, Calculator,
  Loader2, GraduationCap, BookOpen, Filter, Sparkles,
  Bell, ArrowRight, EyeOff
} from 'lucide-react';

export function MotorMedias({ year, trimestre, classGroupId, teacherId }) {
  const navigate = useNavigate();
  const admin = (() => { try { return isAdmin(); } catch { return false; } })();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [statusFilter, setStatusFilter] = useState('todos');
  const [calculating, setCalculating] = useState(false);
  const [config, setConfig] = useState(null);
  const [pendenciasAlertas, setPendenciasAlertas] = useState([]); // alertas do último Calcular
  const [ignorados, setIgnorados] = useState(new Set()); // ids dos alertas ignorados (admin)
  const [notificandoId, setNotificandoId] = useState(null);
  const [lastResult, setLastResult] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [showAnalytics, setShowAnalytics] = useState(false);
  const [tasks, setTasks] = useState([]);
  const [historico, setHistorico] = useState(null);
  const [showHistorico, setShowHistorico] = useState(false);
  
  // Escopo do cálculo: "todos" | "turma" | "aluno"
  const [scope, setScope] = useState('todos');
  const [scopeClassId, setScopeClassId] = useState('');
  const [scopeStudentId, setScopeStudentId] = useState('');
  const [availableClasses, setAvailableClasses] = useState([]);
  const [availableStudents, setAvailableStudents] = useState([]);
  const [calculatingBucket, setCalculatingBucket] = useState(null);
  
  const calcularBucket = async (b) => {
    setCalculatingBucket(`${b.class_group_id}__${b.subject_id}`);
    try {
      const res = await api.post('/grade-engine/calcular-medias', {
        year: parseInt(year),
        trimestre: trimestre,
        class_group_id: b.class_group_id,
        subject_id: b.subject_id,
        force: false
      });
      const d = res.data;
      if (d.blocked) {
        toast.warning(`⚠ ${b.class_group_name} · ${b.subject_name}: ${d.alertas?.length || 0} pendência(s) bloqueando`);
        setPendenciasAlertas(d.alertas || []);
        setLastResult(d);
      } else {
        toast.success(`✓ ${b.class_group_name} · ${b.subject_name}: ${d.medias_persistidas} média(s) calculada(s)`);
        await loadData();
      }
    } catch {
      toast.error('Erro ao calcular bucket');
    } finally {
      setCalculatingBucket(null);
    }
  };
  
  useEffect(() => {
    api.get('/class-groups').then(r => {
      const arr = Array.isArray(r.data) ? r.data : (r.data?.items || []);
      setAvailableClasses(arr);
    }).catch(() => {});
  }, []);
  
  useEffect(() => {
    if (scope === 'aluno' && scopeClassId) {
      api.get(`/students?class_group_id=${scopeClassId}&limit=500`).then(r => {
        const arr = Array.isArray(r.data) ? r.data : (r.data?.students || r.data?.items || []);
        setAvailableStudents(arr.filter(a => a.id && a.name));
      }).catch(() => setAvailableStudents([]));
    } else {
      setAvailableStudents([]);
    }
  }, [scope, scopeClassId]);

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [year, trimestre, classGroupId, teacherId]);

  useEffect(() => {
    api.get('/grade-engine/config').then(r => setConfig(r.data)).catch(() => {});
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (year) params.append('year', year);
      if (trimestre) params.append('trimestre', trimestre);
      if (classGroupId) params.append('class_group_id', classGroupId);
      if (teacherId) params.append('teacher_id', teacherId);
      const res = await api.get(`/grade-engine/status-global?${params}`);
      setData(res.data);
    } catch (e) {
      toast.error('Erro ao carregar status do motor');
    } finally {
      setLoading(false);
    }
  };

  const handleCalcular = async (force = false) => {
    setCalculating(true);
    try {
      const payload = {
        year: parseInt(year),
        trimestre: trimestre || null,
        class_group_id: classGroupId || null,
        force
      };
      // Aplicar escopo selecionado
      if (scope === 'turma' && scopeClassId) {
        payload.class_group_id = scopeClassId;
      } else if (scope === 'aluno' && scopeStudentId && scopeClassId) {
        payload.class_group_id = scopeClassId;
        payload.student_ids = [scopeStudentId];
      }
      const res = await api.post('/grade-engine/calcular-medias', payload);
      const d = res.data;
      setLastResult(d);
      if (d.blocked) {
        // BLOQUEADO: exibir alertas
        setPendenciasAlertas(d.alertas || []);
        toast.error(`⚠ Cálculo bloqueado: ${d.alertas?.length || 0} bucket(s) com lançamentos pendentes`);
      } else {
        setPendenciasAlertas([]);
        toast.success(`✓ ${d.medias_persistidas} média(s) calculada(s)${d.forced ? ' (FORÇADO)' : ''}.`);
        await loadData();
      }
    } catch (e) {
      toast.error('Erro ao calcular médias');
    } finally {
      setCalculating(false);
    }
  };

  const handleNotificar = async (alerta) => {
    setNotificandoId(alerta.id);
    try {
      const res = await api.post('/grade-engine/notificar-professor', {
        professor_id: alerta.professor_id,
        professor_nome: alerta.professor_nome,
        class_group_id: alerta.class_group_id,
        class_group_name: alerta.class_group_name,
        subject_id: alerta.subject_id,
        subject_name: alerta.subject_name,
        trimestre: alerta.trimestre,
        faltantes: alerta.faltantes,
        canais: ['interno', 'email']
      });
      if (res.data?.success) {
        const enviados = res.data.canais_enviados || [];
        const falhos = res.data.canais_falhos || [];
        if (enviados.includes('email')) {
          toast.success(`✓ ${alerta.professor_nome}: notificação interna + e-mail enviado`);
        } else if (falhos.length > 0) {
          const erro = falhos[0]?.erro || 'erro desconhecido';
          toast.warning(`⚠ Lembrete interno criado, mas e-mail falhou: ${erro}`);
        } else {
          toast.success(res.data.message);
        }
      }
    } catch (e) {
      toast.error('Erro ao notificar professor');
    } finally {
      setNotificandoId(null);
    }
  };

  const handleIrParaLancamento = (alerta) => {
    // Deep link para Portal Professor com filtros pré-aplicados
    const params = new URLSearchParams({
      class_group_id: alerta.class_group_id,
      subject_id: alerta.subject_id,
      trimestre: alerta.trimestre
    });
    navigate(`/teacher-portal?${params}`);
  };

  const handleIgnorar = (id) => setIgnorados(s => new Set([...s, id]));

  const loadAnalytics = async () => {
    try {
      const res = await api.get(`/grade-engine/lembretes/analytics?days=30${trimestre ? `&trimestre=${encodeURIComponent(trimestre)}` : ''}`);
      setAnalytics(res.data);
      setShowAnalytics(true);
      if (res.data.auto_tasks_criadas > 0) {
        toast.warning(`⚠ ${res.data.auto_tasks_criadas} task(s) de coordenador criada(s) automaticamente`);
      }
      // Carregar tasks abertas
      const t = await api.get('/grade-engine/coordenador-tasks?status=abertas');
      setTasks(t.data?.tasks || []);
    } catch (e) {
      toast.error('Erro ao carregar analytics');
    }
  };

  const fecharTask = async (taskId) => {
    const obs = window.prompt('Observação do fechamento (opcional):') || '';
    try {
      await api.post(`/grade-engine/coordenador-tasks/${taskId}/fechar`, { observacao: obs });
      toast.success('Task fechada');
      const t = await api.get('/grade-engine/coordenador-tasks?status=abertas');
      setTasks(t.data?.tasks || []);
    } catch {
      toast.error('Erro ao fechar task');
    }
  };

  const loadHistorico = async () => {
    try {
      const r = await api.get('/grade-engine/coordenador-tasks/historico?months=12');
      setHistorico(r.data);
      setShowHistorico(true);
    } catch {
      toast.error('Erro ao carregar histórico');
    }
  };

  const alertasVisiveis = useMemo(
    () => pendenciasAlertas.filter(a => !ignorados.has(a.id)),
    [pendenciasAlertas, ignorados]
  );

  const buckets = useMemo(() => {
    if (!data?.buckets) return [];
    if (statusFilter === 'todos') return data.buckets;
    return data.buckets.filter(b => b.status === statusFilter);
  }, [data, statusFilter]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
      </div>
    );
  }

  const kpis = data?.kpis || {};

  return (
    <div className="space-y-6" data-testid="motor-medias">
      {/* CARD DE ALERTA — pendências do último Calcular */}
      {alertasVisiveis.length > 0 && (
        <div className="bg-gradient-to-r from-red-50 to-amber-50 border-2 border-red-300 rounded-2xl p-5 shadow-sm" data-testid="alerta-pendencias">
          <div className="flex items-start gap-3 mb-4">
            <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
              <AlertTriangle className="w-5 h-5 text-red-600" />
            </div>
            <div className="flex-1">
              <h3 className="font-bold text-slate-800 text-lg">Atenção: Lançamentos Pendentes</h3>
              <p className="text-sm text-slate-600">
                {alertasVisiveis.length} situação(ões) bloqueando o cálculo. Notifique o professor ou direcione-o para o lançamento.
              </p>
            </div>
            {admin && lastResult?.completos_disponiveis > 0 && (
              <Button
                size="sm"
                variant="outline"
                className="border-red-400 text-red-700 hover:bg-red-100"
                onClick={() => handleCalcular(true)}
                disabled={calculating}
                data-testid="btn-forcar-calculo"
              >
                Forçar Cálculo Mesmo Assim ({lastResult.completos_disponiveis})
              </Button>
            )}
          </div>
          <div className="space-y-2">
            {alertasVisiveis.map(a => (
              <div key={a.id} className="bg-white rounded-xl p-3 border border-red-200" data-testid={`alerta-${a.id}`}>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="flex-1 min-w-[260px]">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-slate-800">{a.professor_nome}</span>
                      <span className="text-slate-400">•</span>
                      <span className="text-slate-700">{a.class_group_name}</span>
                      <span className="text-slate-400">•</span>
                      <span className="text-slate-700">{a.subject_name}</span>
                      <Badge variant="outline" className="text-[10px]">{a.trimestre}</Badge>
                    </div>
                    <div className="flex flex-wrap gap-1 mt-2">
                      <span className="text-xs text-slate-500 mr-1">Faltam:</span>
                      {a.faltantes.map(f => (
                        <Badge key={f} className="bg-red-100 text-red-700 text-[10px] border border-red-200">{f}</Badge>
                      ))}
                      <span className="text-xs text-slate-500 ml-2">{a.alunos_pendentes} aluno(s) afetado(s)</span>
                    </div>
                  </div>
                  <div className="flex gap-2 items-center">
                    <Button
                      size="sm"
                      onClick={() => handleNotificar(a)}
                      disabled={notificandoId === a.id}
                      className="bg-amber-500 hover:bg-amber-600 text-white"
                      data-testid={`btn-notificar-${a.id}`}
                    >
                      {notificandoId === a.id ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Bell className="w-3 h-3 mr-1" />}
                      Notificar Professor
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleIrParaLancamento(a)}
                      className="border-purple-300 text-purple-700 hover:bg-purple-50"
                      data-testid={`btn-ir-lancar-${a.id}`}
                    >
                      <ArrowRight className="w-3 h-3 mr-1" />
                      Ir para Lançamento
                    </Button>
                    {admin && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleIgnorar(a.id)}
                        className="text-slate-500 hover:bg-slate-100"
                        data-testid={`btn-ignorar-${a.id}`}
                        title="Apenas administradores podem ignorar"
                      >
                        <EyeOff className="w-3 h-3" />
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Painel Histórico de Conversas */}
      {showHistorico && historico && (
        <div className="bg-gradient-to-br from-rose-50 to-orange-50 border border-rose-200 rounded-2xl p-5" data-testid="historico-panel">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="font-bold text-slate-800">📊 Histórico de Conversas com Coordenador</h3>
              <p className="text-xs text-slate-500">Últimos {historico.periodo_meses} meses · KPI pedagógico</p>
            </div>
            <Button size="sm" variant="ghost" onClick={() => setShowHistorico(false)} className="text-slate-500">
              Ocultar
            </Button>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
            <KpiCard label="Total tasks" value={historico.kpis.total_tasks} color="purple" />
            <KpiCard label="Fechadas" value={historico.kpis.fechadas} color="emerald" />
            <KpiCard label="Em Aberto" value={historico.kpis.abertas} color="amber" />
            <KpiCard label="Recorrentes" value={historico.kpis.professores_recorrentes} subtitle="≥2 conversas" color="red" />
            <KpiCard label="Taxa Melhoria" value={`${historico.kpis.taxa_melhoria_pct}%`} subtitle={`${historico.kpis.tasks_com_dados_pos} comparadas`} color="emerald" />
          </div>

          {/* Série mensal */}
          {historico.serie_mensal?.length > 0 && (
            <div className="bg-white rounded-xl border border-slate-100 p-4 mb-4">
              <p className="text-sm font-semibold text-slate-700 mb-3">Conversas por mês</p>
              <div className="flex items-end gap-2 h-24">
                {historico.serie_mensal.slice(-12).map(m => {
                  const max = Math.max(...historico.serie_mensal.map(x => x.total));
                  const h = max > 0 ? (m.total / max) * 100 : 0;
                  return (
                    <div key={m.mes} className="flex-1 flex flex-col items-center gap-1">
                      <div className="w-full bg-slate-100 rounded-t-md flex items-end" style={{ height: '80px' }}>
                        <div
                          className="w-full bg-gradient-to-t from-rose-500 to-rose-300 rounded-t-md transition-all"
                          style={{ height: `${h}%` }}
                          title={`${m.mes}: ${m.total} (${m.fechadas} fechadas)`}
                        />
                      </div>
                      <span className="text-[9px] text-slate-500">{m.mes.slice(5)}</span>
                      <span className="text-xs font-semibold text-slate-700">{m.total}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Melhorias */}
          {historico.melhorias?.length > 0 && (
            <div className="bg-white rounded-xl border border-slate-100 overflow-hidden mb-4">
              <div className="px-4 py-2 border-b bg-emerald-50 text-xs font-semibold text-emerald-800">
                Impacto pós-conversa (top 10 deltas)
              </div>
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-600">
                  <tr>
                    <th className="text-left px-3 py-2">Professor</th>
                    <th className="text-center px-3 py-2">Antes</th>
                    <th className="text-center px-3 py-2">Depois</th>
                    <th className="text-center px-3 py-2">Δ</th>
                  </tr>
                </thead>
                <tbody>
                  {historico.melhorias.slice(0, 10).map(m => (
                    <tr key={m.task_id} className="border-t border-slate-100">
                      <td className="px-3 py-2 font-medium">{m.professor_nome}</td>
                      <td className="px-3 py-2 text-center font-mono text-slate-500">{m.taxa_antes}%</td>
                      <td className="px-3 py-2 text-center font-mono">{m.taxa_depois}%</td>
                      <td className="px-3 py-2 text-center font-mono font-bold" style={{ color: m.melhorou ? '#10B981' : '#EF4444' }}>
                        {m.delta > 0 ? '+' : ''}{m.delta}pp
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Recorrentes */}
          {historico.recorrentes?.length > 0 && (
            <div className="bg-white rounded-xl border border-slate-100 overflow-hidden">
              <div className="px-4 py-2 border-b bg-red-50 text-xs font-semibold text-red-800">
                ⚠ Professores recorrentes (≥2 conversas)
              </div>
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-600">
                  <tr>
                    <th className="text-left px-3 py-2">Professor</th>
                    <th className="text-center px-3 py-2">Total</th>
                    <th className="text-center px-3 py-2">Fechadas</th>
                    <th className="text-center px-3 py-2">Abertas</th>
                    <th className="text-center px-3 py-2">Última</th>
                  </tr>
                </thead>
                <tbody>
                  {historico.recorrentes.map(r => (
                    <tr key={r.professor_id} className="border-t border-slate-100">
                      <td className="px-3 py-2 font-medium">{r.professor_nome}</td>
                      <td className="px-3 py-2 text-center font-bold">{r.total_tasks}</td>
                      <td className="px-3 py-2 text-center text-emerald-600">{r.fechadas}</td>
                      <td className="px-3 py-2 text-center text-amber-600">{r.abertas}</td>
                      <td className="px-3 py-2 text-center text-xs text-slate-500">{r.ultima_em ? r.ultima_em.slice(0, 10) : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Painel Analytics de Engajamento */}
      {showAnalytics && analytics && (
        <div className="bg-gradient-to-br from-indigo-50 to-purple-50 border border-indigo-200 rounded-2xl p-5" data-testid="analytics-panel">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="font-bold text-slate-800">Engajamento dos Professores</h3>
              <p className="text-xs text-slate-500">Últimos {analytics.periodo_dias} dias · notificações de pendências</p>
            </div>
            <Button size="sm" variant="ghost" onClick={() => setShowAnalytics(false)} className="text-slate-500">
              Ocultar
            </Button>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
            <KpiCard label="Total" value={analytics.kpis.total_notificacoes} color="purple" />
            <KpiCard label="E-mails" value={analytics.kpis.enviados_por_email} color="purple" />
            <KpiCard label="Abertos" value={`${analytics.kpis.abertos} (${analytics.kpis.taxa_abertura_pct}%)`} color="emerald" />
            <KpiCard label="Tempo médio" value={analytics.kpis.tempo_medio_resposta_min !== null ? `${analytics.kpis.tempo_medio_resposta_min} min` : '—'} color="emerald" subtitle="resposta" />
            <KpiCard label="Mediana" value={analytics.kpis.tempo_mediano_resposta_min !== null ? `${analytics.kpis.tempo_mediano_resposta_min} min` : '—'} color="amber" />
          </div>
          {tasks.length > 0 && (
            <div className="bg-amber-50 border-2 border-amber-300 rounded-xl p-4 mb-4" data-testid="coord-tasks-panel">
              <div className="flex items-center gap-2 mb-3">
                <AlertTriangle className="w-5 h-5 text-amber-600" />
                <h4 className="font-bold text-amber-900">⚠ Tasks de Coordenador (Trigger Automático)</h4>
                <Badge className="bg-amber-600 text-white ml-auto">{tasks.length}</Badge>
              </div>
              <div className="space-y-2">
                {tasks.map(t => (
                  <div key={t.id} className="bg-white rounded-lg p-3 border border-amber-200 flex items-start justify-between gap-3" data-testid={`task-${t.id}`}>
                    <div className="flex-1">
                      <p className="font-semibold text-slate-800 text-sm">{t.titulo}</p>
                      <p className="text-xs text-slate-600 mt-1">{t.descricao}</p>
                      <div className="flex flex-wrap gap-2 mt-2 text-[10px] text-slate-500">
                        <span>Total: {t.metricas?.total_notificacoes}</span>
                        <span>•</span>
                        <span>Não abertos: {t.metricas?.nao_abertos}</span>
                        <span>•</span>
                        <span>Taxa: {t.metricas?.taxa_abertura}%</span>
                      </div>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                      onClick={() => fecharTask(t.id)}
                      data-testid={`btn-fechar-task-${t.id}`}
                    >
                      <CheckCircle2 className="w-3 h-3 mr-1" />
                      Resolver
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}
          {analytics.ranking_professores?.length > 0 && (
            <div className="bg-white rounded-xl border border-slate-100 overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-600">
                  <tr>
                    <th className="text-left px-3 py-2">#</th>
                    <th className="text-left px-3 py-2">Professor</th>
                    <th className="text-center px-3 py-2">Notif.</th>
                    <th className="text-center px-3 py-2">Abertos</th>
                    <th className="text-center px-3 py-2">Taxa</th>
                    <th className="text-center px-3 py-2">Tempo Médio</th>
                  </tr>
                </thead>
                <tbody>
                  {analytics.ranking_professores.slice(0, 10).map((r, i) => (
                    <tr key={r.professor_id} className="border-t border-slate-100">
                      <td className="px-3 py-2 text-slate-500">{i + 1}</td>
                      <td className="px-3 py-2 font-medium">{r.professor_nome || r.professor_id}</td>
                      <td className="px-3 py-2 text-center">{r.total_notificacoes}</td>
                      <td className="px-3 py-2 text-center text-emerald-600 font-medium">{r.abertos}</td>
                      <td className="px-3 py-2 text-center font-mono">{r.taxa_abertura}%</td>
                      <td className="px-3 py-2 text-center font-mono text-xs">{r.tempo_medio_resposta_min !== null ? `${r.tempo_medio_resposta_min} min` : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Cabeçalho do Motor */}
      <div className="bg-gradient-to-r from-purple-50 via-white to-emerald-50 border border-purple-100 rounded-2xl p-5 flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-600 to-emerald-500 flex items-center justify-center shadow-md">
            <Calculator className="w-6 h-6 text-white" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-slate-800">Motor Central de Médias</h3>
            <p className="text-xs text-slate-500">
              {config?.formula_trimestral || 'Fórmula: (APA + Prontidão + Trabalho + Avaliação Trimestral) / 4'}
            </p>
          </div>
        </div>
        <div className="flex gap-2 items-center">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[180px] h-9 bg-white" data-testid="filtro-status">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="todos">Todos os status</SelectItem>
              <SelectItem value="COMPLETO">✅ Prontos para fechar</SelectItem>
              <SelectItem value="PARCIAL">⚠ Parciais</SelectItem>
              <SelectItem value="CRITICO">🚫 Críticos</SelectItem>
            </SelectContent>
          </Select>
          <Button
            variant="outline"
            size="sm"
            onClick={loadAnalytics}
            className="border-indigo-300 text-indigo-700 hover:bg-indigo-50"
            data-testid="btn-analytics"
          >
            <Sparkles className="w-4 h-4 mr-1" />
            Engajamento
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={loadHistorico}
            className="border-rose-300 text-rose-700 hover:bg-rose-50"
            data-testid="btn-historico"
          >
            <Sparkles className="w-4 h-4 mr-1" />
            Histórico
          </Button>
          <Select value={scope} onValueChange={setScope}>
            <SelectTrigger className="w-[130px] h-9 bg-white" data-testid="scope-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="todos">Toda escola</SelectItem>
              <SelectItem value="turma">Por turma</SelectItem>
              <SelectItem value="aluno">Por aluno</SelectItem>
            </SelectContent>
          </Select>
          {(scope === 'turma' || scope === 'aluno') && (
            <Select value={scopeClassId} onValueChange={setScopeClassId}>
              <SelectTrigger className="w-[150px] h-9 bg-white" data-testid="scope-turma">
                <SelectValue placeholder="Turma..." />
              </SelectTrigger>
              <SelectContent className="bg-white max-h-[300px]">
                {availableClasses.map(c => (
                  <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          {scope === 'aluno' && scopeClassId && (
            <Select value={scopeStudentId} onValueChange={setScopeStudentId}>
              <SelectTrigger className="w-[180px] h-9 bg-white" data-testid="scope-aluno">
                <SelectValue placeholder="Aluno..." />
              </SelectTrigger>
              <SelectContent className="bg-white max-h-[300px]">
                {availableStudents.map(s => (
                  <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <Button
            onClick={() => handleCalcular(false)}
            disabled={calculating || (scope === 'turma' && !scopeClassId) || (scope === 'aluno' && (!scopeClassId || !scopeStudentId))}
            className="bg-gradient-to-r from-purple-600 to-emerald-500 text-white hover:opacity-90"
            data-testid="btn-calcular-medias"
          >
            {calculating ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Calculando…</>
            ) : (
              <><Sparkles className="w-4 h-4 mr-2" />Calcular Médias</>
            )}
          </Button>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiCard icon={<GraduationCap className="w-5 h-5" />} label="Turmas Completas" value={`${kpis.turmas_completas || 0}/${kpis.total_turmas || 0}`} percent={kpis.percentual_turmas_completas || 0} color="emerald" testid="kpi-turmas-completas" />
        <KpiCard icon={<BookOpen className="w-5 h-5" />} label="Disciplinas Completas" value={`${kpis.disciplinas_completas || 0}/${kpis.total_disciplinas || 0}`} percent={kpis.percentual_disciplinas_completas || 0} color="emerald" testid="kpi-disc-completas" />
        <KpiCard icon={<AlertTriangle className="w-5 h-5" />} label="Disciplinas Parciais" value={kpis.disciplinas_parciais || 0} subtitle="alguns alunos sem nota" color="amber" testid="kpi-disc-parciais" />
        <KpiCard icon={<XCircle className="w-5 h-5" />} label="Disciplinas Críticas" value={kpis.disciplinas_criticas || 0} subtitle="nenhum aluno completo" color="red" testid="kpi-disc-criticas" />
      </div>

      {/* Lista de buckets */}
      {buckets.length === 0 ? (
        <div className="text-center py-10 bg-white rounded-xl border border-slate-200">
          <Filter className="w-8 h-8 mx-auto text-slate-300 mb-2" />
          <p className="text-slate-500">Nenhum bucket para os filtros selecionados</p>
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wide">
              <tr>
                <th className="text-left px-4 py-3">Turma</th>
                <th className="text-left px-4 py-3">Disciplina</th>
                <th className="text-center px-4 py-3">Alunos</th>
                <th className="text-center px-4 py-3">Completos</th>
                <th className="text-center px-4 py-3">Progresso</th>
                <th className="text-center px-4 py-3">Média Turma</th>
                <th className="text-center px-4 py-3">Status</th>
                <th className="text-center px-4 py-3">Ação</th>
              </tr>
            </thead>
            <tbody>
              {buckets.map((b, i) => (
                <tr key={`${b.class_group_id}-${b.subject_id}-${i}`} className="border-t border-slate-100 hover:bg-slate-50">
                  <td className="px-4 py-3 font-medium text-slate-700">{b.class_group_name}</td>
                  <td className="px-4 py-3 text-slate-600">{b.subject_name}</td>
                  <td className="px-4 py-3 text-center">{b.total_alunos}</td>
                  <td className="px-4 py-3 text-center text-emerald-600 font-medium">{b.alunos_completos}</td>
                  <td className="px-4 py-3 text-center">
                    <div className="inline-flex items-center gap-2">
                      <div className="w-20 h-2 bg-slate-100 rounded-full overflow-hidden">
                        <div className="h-full transition-all" style={{ width: `${b.percentual_completo}%`, backgroundColor: b.cor }} />
                      </div>
                      <span className="text-xs text-slate-500">{b.percentual_completo}%</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-center font-mono text-slate-700">
                    {b.media_turma !== null && b.media_turma !== undefined ? b.media_turma.toFixed(2) : '—'}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <StatusPill status={b.status} cor={b.cor} />
                  </td>
                  <td className="px-4 py-3 text-center">
                    {b.status === 'COMPLETO' ? (
                      <Button
                        size="sm"
                        className="bg-emerald-600 hover:bg-emerald-700 text-white h-7 px-3 text-xs"
                        onClick={() => calcularBucket(b)}
                        disabled={calculatingBucket === `${b.class_group_id}__${b.subject_id}`}
                        data-testid={`btn-calc-bucket-${b.class_group_id}-${b.subject_id}`}
                      >
                        {calculatingBucket === `${b.class_group_id}__${b.subject_id}` ? (
                          <Loader2 className="w-3 h-3 animate-spin" />
                        ) : (
                          <><Sparkles className="w-3 h-3 mr-1" />Calcular</>
                        )}
                      </Button>
                    ) : (
                      <span className="text-xs text-slate-400">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function KpiCard({ icon, label, value, subtitle, percent, color, testid }) {
  const colors = {
    emerald: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    amber: 'bg-amber-50 text-amber-700 border-amber-200',
    red: 'bg-red-50 text-red-700 border-red-200',
    purple: 'bg-purple-50 text-purple-700 border-purple-200'
  };
  return (
    <div className={`rounded-xl border p-4 ${colors[color] || colors.purple}`} data-testid={testid}>
      <div className="flex items-center gap-2 mb-2 opacity-80">
        {icon}
        <span className="text-xs font-medium uppercase tracking-wide">{label}</span>
      </div>
      <div className="text-2xl font-bold">{value}</div>
      {subtitle && <div className="text-xs opacity-70 mt-1">{subtitle}</div>}
      {percent !== undefined && <div className="text-xs opacity-70 mt-1">{percent}%</div>}
    </div>
  );
}

function StatusPill({ status, cor }) {
  const labels = { COMPLETO: 'Completo', PARCIAL: 'Parcial', CRITICO: 'Crítico', SEM_ALUNOS: 'Sem alunos' };
  const Icon = status === 'COMPLETO' ? CheckCircle2 : status === 'PARCIAL' ? AlertTriangle : XCircle;
  return (
    <Badge className="text-xs font-medium" style={{ backgroundColor: cor + '20', color: cor, border: `1px solid ${cor}40` }}>
      <Icon className="w-3 h-3 mr-1" />
      {labels[status] || status}
    </Badge>
  );
}

export default MotorMedias;
